import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const FUNIS_DEFAULT = [
  { key: 'advocacia_sdr',    label: 'Advocacia — SDR',    cor: '#4A90D9', cor_fundo: '#EBF4FF', setor: 'sdr',          segmento: 'advocacia',    etapas: ['Entrada', 'Qualificação', 'Solicitar Documentação'] },
  { key: 'advocacia_closer', label: 'Advocacia — Closer', cor: '#C9A84C', cor_fundo: '#FFFBEB', setor: 'closer',       segmento: 'advocacia',    etapas: ['Marcar Horário de Call', 'Call Realizada'] },
  { key: 'advocacia_adm',    label: 'Advocacia — Adm',    cor: '#9B7FD4', cor_fundo: '#F5F0FF', setor: 'adm',          segmento: 'advocacia',    etapas: ['Enviar Proposta', 'Follow Pós Call', 'Pegar Documentação', 'Gerar Contrato', 'Cobrar Assinatura'] },
  { key: 'saude',            label: 'Saúde',              cor: '#D4789B', cor_fundo: '#FFF0F5', setor: 'saude',        segmento: 'saude',        etapas: ['Qualificação', 'Proposta', 'Follow', 'Fechamento'] },
  { key: 'infoprodutos',     label: 'Infoprodutos',       cor: '#5BAD8F', cor_fundo: '#F0FFF8', setor: 'infoprodutos', segmento: 'infoprodutos', etapas: ['Primeiro Contato', 'Qualificação', 'Proposta', 'Negociação', 'Fechamento'] },
];

const PERFIL_ACESSO = {
  admin:        FUNIS_DEFAULT.map(f => f.key),
  gestor:       FUNIS_DEFAULT.map(f => f.key),
  sdr:          ['advocacia_sdr', 'saude'],
  closer:       ['advocacia_closer'],
  adm:          ['advocacia_adm'],
  infoprodutos: ['infoprodutos'],
};

const PROXIMA_FASE = {
  advocacia_sdr:    'advocacia_closer',
  advocacia_closer: 'advocacia_adm',
  advocacia_adm:    null,
  saude:            null,
  infoprodutos:     null,
};

const LABEL_PROXIMA_FASE = {
  advocacia_sdr:    'Enviar para Closer →',
  advocacia_closer: 'Enviar para Adm →',
};

const prioridadeConfig = {
  alta:  { cor: '#E05454', label: 'Alta' },
  media: { cor: '#C9A84C', label: 'Média' },
  baixa: { cor: '#5BAD8F', label: 'Baixa' },
};

// ── Cores de projeto ─────────────────────────────────────────────────────────
const CORES_PROJETO = ['#4A90D9','#9B7FD4','#5BAD8F','#D4789B','#C9A84C','#E05454','#0891b2','#E07B39'];
function corProjeto(nome) {
  if (!nome) return null;
  let hash = 0;
  for (let i = 0; i < nome.length; i++) hash = nome.charCodeAt(i) + ((hash << 5) - hash);
  return CORES_PROJETO[Math.abs(hash) % CORES_PROJETO.length];
}

const STATUS_QUALIF = {
  qualificado:    { label: 'Qualificado',   cor: '#16a34a', bg: '#dcfce7' },
  em_analise:     { label: 'Em análise',    cor: '#d97706', bg: '#fef3c7' },
  desqualificado: { label: 'Desqualificado',cor: '#dc2626', bg: '#fee2e2' },
};

const URL_REVISAO_CONTRATUAL = 'https://revisao-contratual-bancaria-production.up.railway.app';
const URL_SUPERENDIVIDAMENTO = 'https://calculadora-direito-bancario-production.up.railway.app';

const ORIGENS = {
  instagram:  { label: 'Instagram',  emoji: '📸' },
  tiktok:     { label: 'TikTok',     emoji: '🎵' },
  trafego:    { label: 'Tráfego',    emoji: '📢' },
  indicacao:  { label: 'Indicação',  emoji: '🤝' },
};

// ── Semáforo ──────────────────────────────────────────────────────────────────
function diasNaEtapa(lead) {
  const desde = lead.etapaDesdeParsed;
  if (!desde) return 0;
  return (Date.now() - desde.getTime()) / (1000 * 60 * 60 * 24);
}

function corSemaforo(dias) {
  if (dias < 2)  return '#5BAD8F'; // verde
  if (dias < 3)  return '#C9A84C'; // amarelo
  return '#E05454';                  // vermelho
}

function labelDias(dias) {
  const d = Math.floor(dias);
  if (d === 0) return 'hoje';
  if (d === 1) return '1d';
  return `${d}d`;
}

function dbFunilToLocal(row) {
  return {
    key:      row.key,
    label:    row.label,
    cor:      row.cor,
    corFundo: row.cor_fundo,
    setor:    row.setor,
    segmento: row.segmento,
    etapas:   row.etapas || [],
  };
}

function dbLeadToLocal(row) {
  const etapaDesde = row.etapa_desde ? new Date(row.etapa_desde) : (row.created_at ? new Date(row.created_at) : new Date());
  return {
    id:                row.id,
    nome:              row.nome,
    funil:             row.funil_key,
    etapa:             row.etapa,
    responsavel:       row.responsavel || '',
    prioridade:        row.prioridade || 'media',
    telefone:          row.telefone || '',
    obs:               row.obs || '',
    notas:               Array.isArray(row.notas) ? row.notas : [],
    etapaDesdeParsed:    etapaDesde,
    transferidoDe:       row.transferido_de || null,
    perdido:             row.perdido || false,
    projeto:             row.projeto || '',
    status_qualificacao: row.status_qualificacao || 'em_analise',
    origem:              row.origem || null,
    tem_mensagem_nova:   row.tem_mensagem_nova || false,
    ultima_mensagem_em:  row.ultima_mensagem_em || null,
  };
}

// ── Sino de tarefas no card ───────────────────────────────────────────────────
function iconeSino(tarefasLead) {
  const pendentes = tarefasLead.filter(t => !t.concluida);
  if (pendentes.length === 0) return null;
  const agora = new Date();
  const temVencida = pendentes.some(t => new Date(t.data_hora) <= agora);
  return temVencida ? '🔔' : '🔔';
}

function corSino(tarefasLead) {
  const pendentes = tarefasLead.filter(t => !t.concluida);
  if (pendentes.length === 0) return null;
  const agora = new Date();
  const temVencida = pendentes.some(t => new Date(t.data_hora) <= agora);
  return temVencida ? '#E07B39' : '#aaa';
}

export default function CRM() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const perfilUsuario = usuario?.perfil || 'sdr';
  const [funis, setFunis]               = useState({});
  const [leads, setLeads]               = useState([]);
  const [tarefas, setTarefas]           = useState([]);
  const [carregando, setCarregando]     = useState(true);
  const [funilAtivo, setFunilAtivo]     = useState('advocacia_sdr');
  const [dragId, setDragId]             = useState(null);
  const [dragSobre, setDragSobre]       = useState(null);
  const [cardAberto, setCardAberto]     = useState(null);
  const [etapaOriginal, setEtapaOriginal] = useState(null);
  const [confirmExcluir, setConfirmExcluir] = useState(false);
  const [modalNovo, setModalNovo]       = useState(false);
  const [novoLead, setNovoLead]         = useState({ nome: '', responsavel: '', prioridade: 'media', telefone: '', obs: '', projeto: '', origem: '' });
  const [modalNovoFunil, setModalNovoFunil] = useState(false);
  const [formFunil, setFormFunil]       = useState({ key: '', label: '', cor: '#6B2FA0', corFundo: '#F5F0FF', setor: 'sdr', segmento: 'advocacia', etapas: ['Novo lead'] });
  const [editandoEtapas, setEditandoEtapas] = useState(false);
  const [novaEtapa, setNovaEtapa]       = useState('');
  const [verPerdidos, setVerPerdidos]   = useState(false);
  const [filtroProjeto, setFiltroProjeto] = useState('');
  const [filtroResponsavel, setFiltroResponsavel] = useState('');
  const [filtroBusca, setFiltroBusca]   = useState('');
  const [filtroQualif, setFiltroQualif] = useState('');
  const [filtroOrigem, setFiltroOrigem] = useState('');
  const buscaRef = useRef(null);
  const buscaInputRef = useRef(null);
  // notas/observações
  const [novaObs, setNovaObs]           = useState('');
  const [salvandoNota, setSalvandoNota] = useState(false);
  // tarefas
  const [modalTarefa, setModalTarefa]   = useState(false);
  const [formTarefa, setFormTarefa]     = useState({ titulo: '', data_hora: '' });
  const [salvandoTarefa, setSalvandoTarefa] = useState(false);
  const [colaboradores, setColaboradores]   = useState([]);
  const [projetosCad,   setProjetosCad]     = useState([]);  // projetos cadastrados
  const [analisesRevisaoContratual, setAnalisesRevisaoContratual] = useState([]);
  const [analisesGestaoPassivo, setAnalisesGestaoPassivo] = useState([]);
  const [analisesSuperendividamento, setAnalisesSuperendividamento] = useState([]);

  const dragEtapaIdx = useRef(null);
  const dragSobreEtapaIdx = useRef(null);

  // ── Carregamento inicial ──────────────────────────────────────────────────
  const carregarDados = useCallback(async () => {
    setCarregando(true);

    const { data: funisDb, error: erroFunis } = await supabase
      .from('crm_funis')
      .select('*')
      .order('created_at', { ascending: true });

    if (erroFunis) {
      console.error('Erro ao carregar funis:', erroFunis);
      setCarregando(false);
      return;
    }

    let funisFinais = funisDb || [];
    if (funisFinais.length === 0) {
      const { data: semeados, error: erroSeed } = await supabase
        .from('crm_funis')
        .insert(FUNIS_DEFAULT.map(f => ({
          key: f.key, label: f.label, cor: f.cor,
          cor_fundo: f.cor_fundo, setor: f.setor,
          segmento: f.segmento, etapas: f.etapas,
        })))
        .select();
      if (erroSeed) console.error('Erro ao semear funis:', erroSeed);
      else funisFinais = semeados || [];
    }

    const funisMap = {};
    funisFinais.forEach(row => { funisMap[row.key] = dbFunilToLocal(row); });
    setFunis(funisMap);

    // Paginação real — busca todos sem depender do limite de 1000 do Supabase
    const todosLeads = [];
    let pagina = 0;
    const lote = 1000;
    let erroLeads = null;
    while (true) {
      const { data, error } = await supabase
        .from('crm_leads').select('*')
        .order('created_at', { ascending: false })
        .range(pagina * lote, (pagina + 1) * lote - 1);
      if (error) { erroLeads = error; break; }
      if (!data || data.length === 0) break;
      todosLeads.push(...data);
      if (data.length < lote) break;
      pagina++;
    }

    if (erroLeads) {
      console.error('Erro ao carregar leads:', erroLeads);
    } else {
      const leadsLocais = todosLeads.map(dbLeadToLocal);
      setLeads(leadsLocais);

      // Carrega tarefas e análises de todos os leads — sem filtrar por .in('lead_id', ids):
      // com centenas de leads essa lista de ids gera uma URL de dezenas de milhares de
      // caracteres, que estoura o limite de headers HTTP (~16KB) e falha silenciosamente
      // (bug real encontrado em produção — o filtro por lead já é feito no lado do cliente
      // pelas funções tarefasDoLead/analisesXDoLead, então buscar tudo e filtrar depois é
      // seguro e resolve isso).
      if (leadsLocais.length > 0) {
        const { data: tarefasDb } = await supabase
          .from('crm_tarefas')
          .select('*')
          .order('data_hora', { ascending: true });
        setTarefas(tarefasDb || []);

        // Tabelas novas (revisão contratual, gestão de passivo, superendividamento) — se
        // alguma ainda não existir no banco, falha silenciosamente sem quebrar o resto do CRM.
        const { data: analisesRC, error: erroRC } = await supabase
          .from('analises_revisao_contratual')
          .select('id, lead_id, nome_cliente, resultado, created_at')
          .order('created_at', { ascending: false });
        if (!erroRC) setAnalisesRevisaoContratual(analisesRC || []);

        const { data: analisesGP, error: erroGP } = await supabase
          .from('analises_gestao_passivo')
          .select('id, lead_id, nome_cliente, resultado, created_at')
          .order('created_at', { ascending: false });
        if (!erroGP) setAnalisesGestaoPassivo(analisesGP || []);

        const { data: analisesSE, error: erroSE } = await supabase
          .from('analises_superendividamento')
          .select('id, lead_id, nome_cliente, resultado, created_at')
          .order('created_at', { ascending: false });
        if (!erroSE) setAnalisesSuperendividamento(analisesSE || []);
      }
    }

    const { data: colabDb } = await supabase.from('colaboradores').select('id, nome').ilike('status', 'ativo').order('nome');
    setColaboradores(colabDb || []);

    const { data: projDb } = await supabase.from('projetos').select('id, nome, categoria').order('nome');
    setProjetosCad(projDb || []);

    setCarregando(false);
  }, []);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  function analisesRevisaoContratualDoLead(leadId) {
    return analisesRevisaoContratual.filter(a => a.lead_id === leadId);
  }

  function analisesGestaoPassivoDoLead(leadId) {
    return analisesGestaoPassivo.filter(a => a.lead_id === leadId);
  }

  function analisesSuperendividamentoDoLead(leadId) {
    return analisesSuperendividamento.filter(a => a.lead_id === leadId);
  }

  // ── Derivados ─────────────────────────────────────────────────────────────
  const funisVisiveis  = (PERFIL_ACESSO[perfilUsuario] || []).filter(k => funis[k]);
  const funil          = funis[funilAtivo];

  const leadsDoFunil   = leads.filter(l => l.funil === funilAtivo);

  const projetosNoFunil = [...projetosCad].sort((a,b) => a.nome.localeCompare(b.nome, 'pt'));

  const responsaveisNoFunil = [...new Set(leadsDoFunil.map(l => l.responsavel).filter(Boolean))].sort();

  function aplicarFiltros(lista) {
    const busca = filtroBusca.trim().toLowerCase();
    return lista
      .filter(l => !filtroProjeto || (filtroProjeto === '__sem_projeto__' ? !l.projeto : l.projeto === filtroProjeto))
      .filter(l => !filtroResponsavel || l.responsavel === filtroResponsavel)
      .filter(l => !busca || l.nome.toLowerCase().includes(busca) || l.telefone.toLowerCase().includes(busca))
      .filter(l => !filtroQualif || l.status_qualificacao === filtroQualif)
      .filter(l => !filtroOrigem || l.origem === filtroOrigem);
  }

  const leadsAtivos    = aplicarFiltros(leadsDoFunil.filter(l => !l.perdido));
  const leadsPerdidos  = aplicarFiltros(leadsDoFunil.filter(l => l.perdido));

  function tarefasDoLead(leadId) {
    return tarefas.filter(t => t.lead_id === leadId);
  }

  // ── Funis ─────────────────────────────────────────────────────────────────
  async function criarFunil() {
    if (!formFunil.key || !formFunil.label) return;
    const key = formFunil.key.toLowerCase().replace(/\s+/g, '_');
    const { data, error } = await supabase
      .from('crm_funis')
      .insert({ key, label: formFunil.label, cor: formFunil.cor, cor_fundo: formFunil.corFundo, setor: formFunil.setor, segmento: formFunil.segmento, etapas: formFunil.etapas })
      .select().single();
    if (error) { console.error('Erro ao criar funil:', error); return; }
    setFunis(prev => ({ ...prev, [key]: dbFunilToLocal(data) }));
    setFunilAtivo(key);
    setModalNovoFunil(false);
    setFormFunil({ key: '', label: '', cor: '#6B2FA0', corFundo: '#F5F0FF', setor: 'sdr', segmento: 'advocacia', etapas: ['Novo lead'] });
  }

  // ── Etapas ────────────────────────────────────────────────────────────────
  async function salvarEtapas(novasEtapas) {
    const { error } = await supabase
      .from('crm_funis')
      .update({ etapas: novasEtapas })
      .eq('key', funilAtivo);
    if (error) { console.error('Erro ao salvar etapas:', error); return false; }
    setFunis(prev => ({ ...prev, [funilAtivo]: { ...funil, etapas: novasEtapas } }));
    return true;
  }

  async function adicionarEtapa() {
    if (!novaEtapa.trim() || !funil) return;
    await salvarEtapas([...funil.etapas, novaEtapa.trim()]);
    setNovaEtapa('');
  }

  async function removerEtapa(etapa) {
    if (!funil) return;
    await salvarEtapas(funil.etapas.filter(e => e !== etapa));
  }

  function onDragStartEtapa(idx) { dragEtapaIdx.current = idx; }
  function onDragOverEtapa(e, idx) { e.preventDefault(); dragSobreEtapaIdx.current = idx; }
  async function onDropEtapa() {
    const from = dragEtapaIdx.current;
    const to   = dragSobreEtapaIdx.current;
    if (from === null || to === null || from === to) return;
    const novas = [...funil.etapas];
    const [movida] = novas.splice(from, 1);
    novas.splice(to, 0, movida);
    dragEtapaIdx.current      = null;
    dragSobreEtapaIdx.current = null;
    await salvarEtapas(novas);
  }

  // ── Leads ─────────────────────────────────────────────────────────────────
  async function adicionarLead() {
    if (!novoLead.nome || !funil) return;
    const agora = new Date().toISOString();
    const { data, error } = await supabase
      .from('crm_leads')
      .insert({ nome: novoLead.nome, funil_key: funilAtivo, etapa: funil.etapas[0], responsavel: novoLead.responsavel, prioridade: novoLead.prioridade, telefone: novoLead.telefone, obs: novoLead.obs, projeto: novoLead.projeto || '', origem: novoLead.origem || null, etapa_desde: agora, notas: [] })
      .select().single();
    if (error) { console.error('Erro ao criar lead:', error); return; }
    setLeads(prev => [...prev, dbLeadToLocal(data)]);
    setModalNovo(false);
    setNovoLead({ nome: '', responsavel: '', prioridade: 'media', telefone: '', obs: '', projeto: '', origem: '' });
  }

  async function moverLead(leadId, etapa) {
    const agora = new Date().toISOString();
    const { error } = await supabase.from('crm_leads').update({ etapa, etapa_desde: agora }).eq('id', leadId);
    if (error) { console.error('Erro ao mover lead:', error); return; }
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, etapa, etapaDesdeParsed: new Date(agora) } : l));
    // Registra movimentação como atividade concluída
    const { data: atv } = await supabase.from('crm_tarefas')
      .insert({ lead_id: leadId, titulo: `Movido para "${etapa}"`, data_hora: agora, concluida: true })
      .select().single();
    if (atv) setTarefas(prev => [...prev, atv]);
  }

  async function salvarCard() {
    const agora = new Date().toISOString();
    const etapaMudou = cardAberto.etapa !== etapaOriginal;
    const update = {
      nome:                cardAberto.nome,
      telefone:            cardAberto.telefone,
      responsavel:         cardAberto.responsavel,
      etapa:               cardAberto.etapa,
      prioridade:          cardAberto.prioridade,
      projeto:             cardAberto.projeto || '',
      status_qualificacao: cardAberto.status_qualificacao || 'em_analise',
      origem:              cardAberto.origem || null,
    };
    if (etapaMudou) update.etapa_desde = agora;

    const { error } = await supabase.from('crm_leads').update(update).eq('id', cardAberto.id);
    if (error) { console.error('Erro ao salvar lead:', error); return; }
    setLeads(prev => prev.map(l => l.id === cardAberto.id ? {
      ...l,
      ...cardAberto,
      etapaDesdeParsed: etapaMudou ? new Date(agora) : l.etapaDesdeParsed,
    } : l));
    // Registra mudança de etapa como atividade concluída
    if (etapaMudou) {
      const { data: atv } = await supabase.from('crm_tarefas')
        .insert({ lead_id: cardAberto.id, titulo: `Movido para "${cardAberto.etapa}"`, data_hora: agora, concluida: true })
        .select().single();
      if (atv) setTarefas(prev => [...prev, atv]);
    }
    setCardAberto(null);
    setEtapaOriginal(null);
  }

  // ── Notas (append-only) ───────────────────────────────────────────────────
  async function adicionarNota() {
    if (!novaObs.trim() || !cardAberto) return;
    setSalvandoNota(true);
    const novaNota = { texto: novaObs.trim(), data_hora: new Date().toISOString() };
    const notasAtualizadas = [...(cardAberto.notas || []), novaNota];

    const { error } = await supabase
      .from('crm_leads')
      .update({ notas: notasAtualizadas })
      .eq('id', cardAberto.id);

    if (!error) {
      setCardAberto(prev => ({ ...prev, notas: notasAtualizadas }));
      setLeads(prev => prev.map(l => l.id === cardAberto.id ? { ...l, notas: notasAtualizadas } : l));
      setNovaObs('');
    }
    setSalvandoNota(false);
  }

  // ── Tarefas ───────────────────────────────────────────────────────────────
  async function salvarTarefa() {
    if (!formTarefa.titulo || !formTarefa.data_hora || !cardAberto) return;
    setSalvandoTarefa(true);
    const { data, error } = await supabase
      .from('crm_tarefas')
      .insert({ lead_id: cardAberto.id, titulo: formTarefa.titulo, data_hora: formTarefa.data_hora })
      .select().single();
    if (!error && data) {
      setTarefas(prev => [...prev, data]);
      setFormTarefa({ titulo: '', data_hora: '' });
      setModalTarefa(false);
    }
    setSalvandoTarefa(false);
  }

  async function concluirTarefa(tarefaId) {
    const { error } = await supabase.from('crm_tarefas').update({ concluida: true }).eq('id', tarefaId);
    if (!error) setTarefas(prev => prev.map(t => t.id === tarefaId ? { ...t, concluida: true } : t));
  }

  // ── Outros ────────────────────────────────────────────────────────────────
  async function marcarPerdido(lead) {
    const { error } = await supabase.from('crm_leads').update({ perdido: true }).eq('id', lead.id);
    if (error) { console.error('Erro ao marcar perdido:', error); return; }
    setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, perdido: true } : l));
    setCardAberto(null);
  }

  async function enviarParaContratos(lead) {
    const { error } = await supabase.from('casos').insert([{
      nome_cliente: lead.nome,
      origem_lead_id: lead.id,
      responsavel: lead.responsavel || '',
      pendente_preenchimento: true,
    }]).select().single();
    if (error) { console.error('Erro ao enviar para contratos:', error); return; }
    setCardAberto(null);
    navigate('/casos');
  }

  async function restaurarLead(lead) {
    const { error } = await supabase.from('crm_leads').update({ perdido: false }).eq('id', lead.id);
    if (error) { console.error('Erro ao restaurar lead:', error); return; }
    setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, perdido: false } : l));
  }

  async function excluirLead() {
    const { error } = await supabase.from('crm_leads').delete().eq('id', cardAberto.id);
    if (error) { console.error('Erro ao excluir lead:', error); return; }
    setLeads(prev => prev.filter(l => l.id !== cardAberto.id));
    setTarefas(prev => prev.filter(t => t.lead_id !== cardAberto.id));
    setCardAberto(null);
    setConfirmExcluir(false);
  }

  async function transferirParaProximaFase(lead) {
    const proximoFunil = PROXIMA_FASE[lead.funil];
    if (!proximoFunil || !funis[proximoFunil]) return;
    const primeiraEtapa = funis[proximoFunil].etapas[0];
    const agora = new Date().toISOString();
    const { error } = await supabase
      .from('crm_leads')
      .update({ funil_key: proximoFunil, etapa: primeiraEtapa, transferido_de: lead.funil, etapa_desde: agora })
      .eq('id', lead.id);
    if (error) { console.error('Erro ao transferir lead:', error); return; }
    setLeads(prev => prev.map(l => l.id === lead.id
      ? { ...l, funil: proximoFunil, etapa: primeiraEtapa, transferidoDe: lead.funil, etapaDesdeParsed: new Date(agora) }
      : l
    ));
    setCardAberto(null);
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (carregando) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:300, color:'var(--text-secondary)', fontSize:14 }}>
        Carregando CRM…
      </div>
    );
  }

  if (!funil) {
    return <div style={{ padding:40, textAlign:'center', color:'var(--text-secondary)' }}>Funil não encontrado.</div>;
  }

  return (
    <div>
      {/* ── Barra de funis + ações ── */}
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:20, flexWrap:'wrap' }}>

        {/* Abas de funil */}
        <div style={{ display:'flex', gap:4, background:'white', borderRadius:12, padding:4, border:'1px solid rgba(107,33,168,0.1)', boxShadow:'0 2px 8px rgba(63,0,81,0.06)', flexWrap:'wrap' }}>
          {funisVisiveis.map(key => {
            const f = funis[key];
            if (!f) return null;
            const ativo = funilAtivo === key;
            return (
              <button key={key} onClick={() => { setFunilAtivo(key); setVerPerdidos(false); }}
                style={{
                  padding:'7px 16px', borderRadius:8, cursor:'pointer', fontSize:12.5,
                  fontWeight: ativo ? 700 : 400, transition:'all 0.18s', border:'none',
                  background: ativo ? f.cor : 'transparent',
                  color: ativo ? '#fff' : '#7B5EA7',
                  boxShadow: ativo ? `0 3px 10px ${f.cor}55` : 'none',
                  fontFamily:'Poppins, sans-serif',
                  letterSpacing: ativo ? '0.01em' : '0',
                }}>
                {f.label}
              </button>
            );
          })}
        </div>

        <div style={{ flex:1 }} />

        {/* Botões de ação */}
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button onClick={() => setEditandoEtapas(!editandoEtapas)}
            style={{ padding:'7px 14px', borderRadius:8, border:'1.5px solid rgba(107,33,168,0.18)', background:'white', color:'#7B5EA7', cursor:'pointer', fontSize:12, fontWeight:500, fontFamily:'Poppins,sans-serif', display:'flex', alignItems:'center', gap:5 }}>
            <span style={{ fontSize:13 }}>⚙</span> Etapas
          </button>
          <button onClick={() => setModalNovoFunil(true)}
            style={{ padding:'7px 16px', borderRadius:8, border:'1.5px solid rgba(63,0,81,0.25)', background:'white', color:'#3F0051', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'Poppins,sans-serif' }}>
            + Funil
          </button>
          <button onClick={() => setModalNovo(true)}
            style={{ padding:'8px 20px', borderRadius:9, border:'none', background:`linear-gradient(135deg, ${funil.cor}, ${funil.cor}cc)`, color:'#fff', cursor:'pointer', fontSize:13, fontWeight:700, fontFamily:'Poppins,sans-serif', boxShadow:`0 4px 14px ${funil.cor}55`, letterSpacing:'0.01em' }}>
            + Novo Lead
          </button>
        </div>
      </div>

      {/* Filtros — sempre visíveis, projetos pelo segmento do funil */}
      <div className="filter-bar" style={{ marginBottom:18, flexWrap:'wrap', gap:8 }}>
        <input
          ref={buscaInputRef}
          placeholder="Buscar por nome ou telefone..."
          onChange={e => {
            const v = e.target.value;
            if (buscaRef.current) clearTimeout(buscaRef.current);
            buscaRef.current = setTimeout(() => setFiltroBusca(v), 300);
          }}
          style={{ padding:'7px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:12, fontFamily:'Poppins,sans-serif', background:'var(--bg-secondary)', color:'var(--text-primary)', minWidth:200 }}
        />
        <select value={filtroProjeto} onChange={e => setFiltroProjeto(e.target.value)}
          className={`chip-select${filtroProjeto ? ' active' : ''}`}>
          <option value=''>Projeto</option>
          <option value='__sem_projeto__'>⚠ Sem projeto</option>
          {projetosNoFunil.map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
        </select>
        <select value={filtroResponsavel} onChange={e => setFiltroResponsavel(e.target.value)}
          className={`chip-select${filtroResponsavel ? ' active' : ''}`}>
          <option value=''>Responsável</option>
          {responsaveisNoFunil.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={filtroQualif} onChange={e => setFiltroQualif(e.target.value)}
          className={`chip-select${filtroQualif ? ' active' : ''}`}>
          <option value=''>Qualificação</option>
          {Object.entries(STATUS_QUALIF).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filtroOrigem} onChange={e => setFiltroOrigem(e.target.value)}
          className={`chip-select${filtroOrigem ? ' active' : ''}`}>
          <option value=''>Origem</option>
          {Object.entries(ORIGENS).map(([k,v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
        </select>
        {(filtroProjeto || filtroResponsavel || filtroBusca || filtroQualif || filtroOrigem) && (
          <>
            <button onClick={() => { setFiltroProjeto(''); setFiltroResponsavel(''); setFiltroBusca(''); setFiltroQualif(''); setFiltroOrigem(''); if (buscaInputRef.current) buscaInputRef.current.value = ''; }}
              style={{ padding:'6px 12px', border:'none', borderRadius:8, fontSize:11, background:'#F3EBFF', color:'#7B5EA7', cursor:'pointer', fontWeight:600, fontFamily:'Poppins,sans-serif' }}>
              ✕ Limpar
            </button>
            <span style={{ fontSize:11, color:'#7B5EA7', fontStyle:'italic' }}>
              {leadsAtivos.length} lead(s)
            </span>
          </>
        )}
      </div>

      {/* Editor de etapas com reordenação */}
      {editandoEtapas && (
        <div className="card" style={{ padding:16, marginBottom:20, borderLeft:`4px solid ${funil.cor}` }}>
          <div style={{ fontSize:12, fontWeight:700, color:funil.cor, marginBottom:8, textTransform:'uppercase', letterSpacing:'0.5px' }}>
            Etapas — {funil.label}
          </div>
          <div style={{ fontSize:11, color:'var(--gray-400)', marginBottom:10 }}>Arraste para reordenar</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
            {funil.etapas.map((e, idx) => (
              <div key={e}
                draggable
                onDragStart={() => onDragStartEtapa(idx)}
                onDragOver={ev => onDragOverEtapa(ev, idx)}
                onDrop={onDropEtapa}
                style={{ display:'flex', alignItems:'center', gap:6, background:funil.corFundo, border:`1px solid ${funil.cor}44`, borderRadius:6, padding:'5px 10px', cursor:'grab', userSelect:'none' }}>
                <span style={{ fontSize:12, color:`${funil.cor}88`, marginRight:2 }}>⠿</span>
                <span style={{ fontSize:13, color:funil.cor, fontWeight:500 }}>{e}</span>
                <button onClick={() => removerEtapa(e)}
                  style={{ background:'transparent', border:'none', color:`${funil.cor}99`, cursor:'pointer', fontSize:14, lineHeight:1, padding:0 }}>×</button>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <input placeholder="Nova etapa..." value={novaEtapa} onChange={e => setNovaEtapa(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && adicionarEtapa()}
              style={{ flex:1, padding:'8px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }} />
            <button onClick={adicionarEtapa}
              style={{ padding:'8px 16px', border:'none', borderRadius:6, background:funil.cor, color:'#fff', cursor:'pointer', fontWeight:600, fontSize:13 }}>
              + Adicionar
            </button>
          </div>
        </div>
      )}

      {/* Métricas */}
      <div style={{ display:'flex', gap:12, marginBottom:20, overflowX:'auto', paddingBottom:4 }}>
        {funil.etapas.map(e => {
          const count = leadsAtivos.filter(l => l.etapa === e).length;
          return (
            <div key={e} className="card" style={{ padding:'12px 16px', minWidth:130, flexShrink:0, borderTop:`3px solid ${funil.cor}` }}>
              <div style={{ fontSize:11, color:'var(--gray-400)', marginBottom:4, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{e}</div>
              <div style={{ fontSize:22, fontWeight:700, color:funil.cor }}>{count}</div>
            </div>
          );
        })}
        <div className="card" style={{ padding:'12px 16px', minWidth:130, flexShrink:0, borderTop:'3px solid var(--border)' }}>
          <div style={{ fontSize:11, color:'var(--gray-400)', marginBottom:4 }}>Total no funil</div>
          <div style={{ fontSize:22, fontWeight:700, color:'var(--text-primary)' }}>{leadsAtivos.length}</div>
        </div>
        <div className="card" style={{ padding:'12px 16px', minWidth:130, flexShrink:0, borderTop:'3px solid #16a34a' }}>
          <div style={{ fontSize:11, color:'#16a34a', marginBottom:4 }}>🟢 Ativos hoje</div>
          <div style={{ fontSize:22, fontWeight:700, color:'#16a34a' }}>{leadsAtivos.filter(l => l.ultima_mensagem_em && l.ultima_mensagem_em.slice(0,10) === new Date().toISOString().slice(0,10)).length}</div>
        </div>
        {leadsPerdidos.length > 0 && (
          <div className="card" style={{ padding:'12px 16px', minWidth:130, flexShrink:0, borderTop:'3px solid #E05454', cursor:'pointer', opacity: verPerdidos ? 1 : 0.7 }}
            onClick={() => setVerPerdidos(v => !v)}>
            <div style={{ fontSize:11, color:'#E05454', marginBottom:4 }}>Negócios perdidos</div>
            <div style={{ fontSize:22, fontWeight:700, color:'#E05454' }}>{leadsPerdidos.length}</div>
          </div>
        )}
      </div>

      {/* Negócios perdidos */}
      {verPerdidos && (
        <div style={{ marginBottom:24 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
            <div style={{ width:10, height:10, borderRadius:'50%', background:'#E05454' }} />
            <span style={{ fontSize:13, fontWeight:700, color:'#E05454', textTransform:'uppercase', letterSpacing:'0.4px' }}>Negócios Perdidos</span>
            <button onClick={() => setVerPerdidos(false)}
              style={{ marginLeft:'auto', background:'transparent', border:'none', color:'var(--gray-400)', cursor:'pointer', fontSize:12 }}>
              Ocultar ×
            </button>
          </div>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            {leadsPerdidos.map(lead => {
              const pc = prioridadeConfig[lead.prioridade] || prioridadeConfig.media;
              return (
                <div key={lead.id} className="card"
                  style={{ width:220, padding:14, borderLeft:'4px solid #E05454', opacity:0.8 }}>
                  <div style={{ fontWeight:600, fontSize:13, color:'var(--text-primary)', marginBottom:4 }}>{lead.nome}</div>
                  <div style={{ fontSize:11, color:'var(--gray-400)', marginBottom:6 }}>{lead.etapa} · {lead.telefone}</div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:11, color:'var(--text-secondary)' }}>{lead.responsavel || '—'}</span>
                    <span style={{ fontSize:10, fontWeight:700, color:pc.cor, background:`${pc.cor}18`, padding:'2px 7px', borderRadius:20 }}>{pc.label}</span>
                  </div>
                  <button onClick={() => restaurarLead(lead)}
                    style={{ marginTop:10, width:'100%', background:'transparent', border:'1px solid #E05454', color:'#E05454', borderRadius:6, padding:'5px 0', fontSize:11, cursor:'pointer', fontWeight:600 }}>
                    Reativar lead
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Kanban */}
      <div
        style={{ display:'flex', gap:14, overflowX:'auto', paddingBottom:16, alignItems:'flex-start', cursor:'grab', userSelect:'none' }}
        onMouseDown={e => {
          if (e.button !== 0) return;
          // Não ativa scroll se clicou em um card arrastável
          if (e.target.closest('[draggable="true"]')) return;
          const el = e.currentTarget;
          const startX = e.pageX - el.offsetLeft;
          const scrollLeft = el.scrollLeft;
          el.style.cursor = 'grabbing';
          const onMove = mv => { el.scrollLeft = scrollLeft - (mv.pageX - el.offsetLeft - startX); };
          const onUp = () => { el.style.cursor = 'grab'; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
          window.addEventListener('mousemove', onMove);
          window.addEventListener('mouseup', onUp);
        }}
      >
        {funil.etapas.map(etapa => {
          const cardsEtapa = leadsAtivos.filter(l => l.etapa === etapa)
            .sort((a, b) => (b.tem_mensagem_nova ? 1 : 0) - (a.tem_mensagem_nova ? 1 : 0));
          const sobreEsta  = dragSobre === etapa;
          return (
            <div key={etapa}
              onDragOver={e => { e.preventDefault(); setDragSobre(etapa); }}
              onDragLeave={() => setDragSobre(null)}
              onDrop={() => { if (dragId) moverLead(dragId, etapa); setDragId(null); setDragSobre(null); }}
              style={{
                minWidth:230, maxWidth:250, flexShrink:0,
                background: sobreEsta ? funil.corFundo : 'rgba(243,235,255,0.55)',
                borderRadius:14, padding:12,
                border: sobreEsta ? `2px dashed ${funil.cor}` : '1.5px solid rgba(107,33,168,0.08)',
                transition:'all 0.15s',
                backdropFilter:'blur(4px)',
              }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                  <div style={{ width:10, height:10, borderRadius:'50%', background:funil.cor, flexShrink:0 }} />
                  <span style={{ fontSize:11, fontWeight:700, color:'#7B5EA7', textTransform:'uppercase', letterSpacing:'0.06em' }}>{etapa}</span>
                </div>
                <span style={{ background:funil.cor, color:'#fff', borderRadius:20, padding:'2px 8px', fontSize:11, fontWeight:700 }}>{cardsEtapa.length}</span>
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:10, minHeight:80 }}>
                {cardsEtapa.map(lead => {
                  const pc    = prioridadeConfig[lead.prioridade] || prioridadeConfig.media;
                  const dias  = diasNaEtapa(lead);
                  const semaf = corSemaforo(dias);
                  const tdLead = tarefasDoLead(lead.id);
                  const sinoCor = corSino(tdLead);
                  const analisesLead = analisesRevisaoContratualDoLead(lead.id);
                  const analisesGPLead = analisesGestaoPassivoDoLead(lead.id);
                  const analisesSELead = analisesSuperendividamentoDoLead(lead.id);
                  return (
                    <div key={lead.id} draggable
                      onDragStart={() => setDragId(lead.id)}
                      onDragEnd={() => { setDragId(null); setDragSobre(null); }}
                      onClick={() => {
                        setCardAberto({ ...lead }); setEtapaOriginal(lead.etapa); setNovaObs('');
                      }}
                      className="card kanban-card"
                      style={{ padding:14, cursor:'grab', userSelect:'none', borderLeft:`3px solid ${lead.tem_mensagem_nova ? '#16a34a' : semaf}`, opacity: dragId === lead.id ? 0.4 : 1, transition:'all 0.18s', boxShadow: lead.tem_mensagem_nova ? '0 0 0 2px rgba(22,163,74,0.35)' : undefined }}>

                      {lead.tem_mensagem_nova && (
                        <div style={{ marginBottom:6, display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                          <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:10, fontWeight:700, color:'#16a34a', background:'#dcfce7', padding:'2px 8px', borderRadius:20 }}>
                            💬 Nova mensagem
                          </span>
                          <button onClick={e => {
                            e.stopPropagation();
                            supabase.from('crm_leads').update({ tem_mensagem_nova: false }).eq('id', lead.id).then(() => {
                              setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, tem_mensagem_nova: false } : l));
                            });
                          }} style={{ fontSize:9, fontWeight:700, color:'#16a34a', background:'white', border:'1px solid #16a34a', borderRadius:20, padding:'2px 8px', cursor:'pointer' }}>
                            ✓ ok, já vi
                          </button>
                        </div>
                      )}

                      {!lead.tem_mensagem_nova && lead.ultima_mensagem_em && lead.ultima_mensagem_em.slice(0,10) === new Date().toISOString().slice(0,10) && (
                        <div style={{ marginBottom:6, display:'inline-flex', alignItems:'center', gap:4, fontSize:9, fontWeight:700, color:'#16a34a', background:'#f0fdf4', padding:'2px 7px', borderRadius:20 }}>
                          🟢 Ativo hoje
                        </div>
                      )}

                      {/* Linha topo: nome + sino */}
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                        <div style={{ fontWeight:600, fontSize:13, color:'#1E0A3C', flex:1, marginRight:6, lineHeight:1.3 }}>{lead.nome}</div>
                        {sinoCor && (
                          <span style={{ fontSize:14, color:sinoCor, flexShrink:0 }}>🔔</span>
                        )}
                      </div>

                      <div style={{ fontSize:11, color:'var(--gray-400)', marginBottom:8 }}>{lead.telefone}</div>

                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontSize:11, color:'var(--text-secondary)' }}>{lead.responsavel || '—'}</span>
                        <span style={{ fontSize:10, fontWeight:700, color:pc.cor, background:`${pc.cor}18`, padding:'2px 7px', borderRadius:20 }}>{pc.label}</span>
                      </div>

                      {/* Semáforo — dias na etapa */}
                      <div style={{ display:'flex', alignItems:'center', gap:5, marginTop:8 }}>
                        <div style={{ width:8, height:8, borderRadius:'50%', background:semaf, flexShrink:0 }} />
                        <span style={{ fontSize:10, color:semaf, fontWeight:600 }}>{labelDias(dias)} nesta etapa</span>
                      </div>

                      {lead.transferidoDe && (
                        <div style={{ marginTop:6, fontSize:10, color:'#4A90D9', fontWeight:600, background:'#f0f7ff', padding:'2px 8px', borderRadius:20, display:'inline-block' }}>
                          ↩ de {funis[lead.transferidoDe]?.label || lead.transferidoDe}
                        </div>
                      )}

                      {/* Tag colorida de projeto */}
                      {lead.projeto && (() => {
                        const c = corProjeto(lead.projeto);
                        return (
                          <div style={{ marginTop:5, display:'inline-block', fontSize:10, fontWeight:700, color:c, background:`${c}18`, padding:'2px 9px', borderRadius:20, border:`1px solid ${c}44` }}>
                            {lead.projeto}
                          </div>
                        );
                      })()}

                      {/* Badge de origem */}
                      {lead.origem && ORIGENS[lead.origem] && (
                        <div style={{ marginTop:4, display:'inline-block', fontSize:10, fontWeight:600, color:'#555', background:'#f4f4f8', padding:'2px 8px', borderRadius:20, border:'1px solid #ddd', marginLeft: lead.projeto ? 4 : 0 }}>
                          {ORIGENS[lead.origem].emoji} {ORIGENS[lead.origem].label}
                        </div>
                      )}

                      {/* Badge de qualificação */}
                      {(() => {
                        const sq = STATUS_QUALIF[lead.status_qualificacao] || STATUS_QUALIF.em_analise;
                        return (
                          <div
                            onClick={e => {
                              e.stopPropagation();
                              const ordem = Object.keys(STATUS_QUALIF);
                              const idx = ordem.indexOf(lead.status_qualificacao || 'em_analise');
                              const proximo = ordem[(idx + 1) % ordem.length];
                              supabase.from('crm_leads').update({ status_qualificacao: proximo }).eq('id', lead.id).then(() => {
                                setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, status_qualificacao: proximo } : l));
                              });
                            }}
                            title="Clique para alterar"
                            style={{ marginTop:4, display:'inline-block', fontSize:10, fontWeight:700, color:sq.cor, background:sq.bg, padding:'2px 8px', borderRadius:20, border:`1px solid ${sq.cor}44`, cursor:'pointer', userSelect:'none' }}>
                            {sq.label}
                          </div>
                        );
                      })()}

                      {/* Selos de análises vinculadas (revisão contratual, gestão de passivo, superendividamento) */}
                      {(analisesLead.length > 0 || analisesGPLead.length > 0 || analisesSELead.length > 0) && (
                        <div style={{ marginTop:4, display:'flex', gap:4, flexWrap:'wrap' }}>
                          {analisesLead.length > 0 && (
                            <span title={`${analisesLead.length} análise(s) de revisão contratual`} style={{ fontSize:10, fontWeight:700, color:'#7B2FBE', background:'#F3EBFF', padding:'2px 8px', borderRadius:20, whiteSpace:'nowrap', border:'1px solid #7B2FBE30' }}>
                              ⚖️ {analisesLead.length}
                            </span>
                          )}
                          {analisesGPLead.length > 0 && (
                            <span title={`${analisesGPLead.length} análise(s) de gestão de passivo`} style={{ fontSize:10, fontWeight:700, color:'#0F766E', background:'#ECFDF5', padding:'2px 8px', borderRadius:20, whiteSpace:'nowrap', border:'1px solid #0F766E30' }}>
                              🏢 {analisesGPLead.length}
                            </span>
                          )}
                          {analisesSELead.length > 0 && (
                            <span title={`${analisesSELead.length} análise(s) de superendividamento`} style={{ fontSize:10, fontWeight:700, color:'#B45309', background:'#FFFBEB', padding:'2px 8px', borderRadius:20, whiteSpace:'nowrap', border:'1px solid #B4530930' }}>
                              💳 {analisesSELead.length}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Barra de atividades: tarefas + notas */}
                      {(() => {
                        const concluidas = tdLead.filter(t => t.concluida).length;
                        const pendentes  = tdLead.filter(t => !t.concluida).length;
                        const totalNotas = (lead.notas || []).length;
                        const totalAtiv  = tdLead.length + totalNotas;
                        if (totalAtiv === 0) return null;
                        return (
                          <div style={{ marginTop:8, display:'flex', gap:5, flexWrap:'wrap', borderTop:'1px solid rgba(107,33,168,0.07)', paddingTop:7 }}>
                            {concluidas > 0 && (
                              <span style={{ fontSize:10, fontWeight:700, color:'#059669', background:'#dcfce7', padding:'2px 8px', borderRadius:20, display:'flex', alignItems:'center', gap:3 }}>
                                ✓ {concluidas} feita{concluidas>1?'s':''}
                              </span>
                            )}
                            {pendentes > 0 && (
                              <span style={{ fontSize:10, fontWeight:700, color:'#d97706', background:'#fef3c7', padding:'2px 8px', borderRadius:20, display:'flex', alignItems:'center', gap:3 }}>
                                ⏳ {pendentes} pendente{pendentes>1?'s':''}
                              </span>
                            )}
                            {totalNotas > 0 && (
                              <span style={{ fontSize:10, fontWeight:600, color:'#7B5EA7', background:'#F3EBFF', padding:'2px 8px', borderRadius:20 }}>
                                📝 {totalNotas} nota{totalNotas>1?'s':''}
                              </span>
                            )}
                          </div>
                        );
                      })()}

                      {/* Última nota (preview) */}
                      {lead.notas && lead.notas.length > 0 && (
                        <div style={{ marginTop:6, fontSize:11, color:'var(--gray-400)', fontStyle:'italic', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {lead.notas[lead.notas.length - 1].texto}
                        </div>
                      )}
                    </div>
                  );
                })}
                {cardsEtapa.length === 0 && (
                  <div style={{ textAlign:'center', padding:'20px 0', fontSize:12, color:`${funil.cor}66` }}>
                    Arraste um card aqui
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal card aberto */}
      {cardAberto && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div className="card" style={{ width:500, padding:32, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
              <h3 style={{ margin:0, color: funil.cor }}>{cardAberto.nome}</h3>
              <button onClick={() => { setCardAberto(null); setConfirmExcluir(false); setNovaObs(''); setModalTarefa(false); }}
                style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:'var(--gray-400)' }}>×</button>
            </div>

            {/* Semáforo no modal */}
            {(() => {
              const dias  = diasNaEtapa(cardAberto);
              const semaf = corSemaforo(dias);
              return (
                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:16, padding:'8px 12px', background:`${semaf}11`, borderRadius:8, border:`1px solid ${semaf}44` }}>
                  <div style={{ width:10, height:10, borderRadius:'50%', background:semaf }} />
                  <span style={{ fontSize:12, color:semaf, fontWeight:700 }}>{labelDias(dias)} nesta etapa</span>
                  {dias >= 2 && <span style={{ fontSize:11, color:semaf, marginLeft:4 }}>{dias >= 3 ? '— Vencido' : '— A vencer'}</span>}
                </div>
              );
            })()}

            {[
              { label:'Nome', key:'nome', type:'text' },
              { label:'Telefone', key:'telefone', type:'text' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>{f.label}</label>
                <input type={f.type} value={cardAberto[f.key] || ''} onChange={e => setCardAberto({...cardAberto, [f.key]: e.target.value})}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)', boxSizing:'border-box' }} />
              </div>
            ))}
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Projeto / Cliente</label>
              <select value={cardAberto.projeto || ''} onChange={e => setCardAberto({...cardAberto, projeto: e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                <option value=''>— Selecione —</option>
                {projetosCad.map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Responsável</label>
              <select value={cardAberto.responsavel || ''} onChange={e => setCardAberto({...cardAberto, responsavel: e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                <option value=''>— Selecione —</option>
                {colaboradores.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
              </select>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Etapa</label>
              <select value={cardAberto.etapa} onChange={e => setCardAberto({...cardAberto, etapa: e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                {funil.etapas.map(e => <option key={e}>{e}</option>)}
              </select>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Prioridade</label>
              <select value={cardAberto.prioridade} onChange={e => setCardAberto({...cardAberto, prioridade: e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                {Object.entries(prioridadeConfig).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Origem</label>
              <select value={cardAberto.origem || ''} onChange={e => setCardAberto({...cardAberto, origem: e.target.value || null})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                <option value=''>— Não informado —</option>
                {Object.entries(ORIGENS).map(([k,v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
              </select>
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Qualificação</label>
              <select value={cardAberto.status_qualificacao || 'em_analise'} onChange={e => setCardAberto({...cardAberto, status_qualificacao: e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                {Object.entries(STATUS_QUALIF).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>

            {/* Análises de revisão contratual bancária vinculadas */}
            {analisesRevisaoContratualDoLead(cardAberto.id).length > 0 && (
              <div className="card" style={{ padding:16, marginBottom:14, border:'1px solid #7B2FBE30', background:'#FBF7FF' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#7B2FBE', textTransform:'uppercase', marginBottom:10 }}>⚖️ Revisão contratual bancária</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {analisesRevisaoContratualDoLead(cardAberto.id).map(a => {
                    const nTeses = a.resultado?.matrizTeses?.length || 0;
                    const nCaminhos = a.resultado?.caminhosNaoJudiciais?.length || 0;
                    return (
                      <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, background:'white', borderRadius:10, padding:'10px 12px', border:'1px solid rgba(107,33,168,0.1)' }}>
                        <div>
                          <div style={{ fontSize:12.5, fontWeight:600, color:'#1E0A3C' }}>
                            {nTeses > 0 ? `${nTeses} tese(s) identificada(s)` : 'Nenhuma tese identificada'}
                            {nCaminhos > 0 && ` · ${nCaminhos} caminho(s) não-judicial(is)`}
                          </div>
                          <div style={{ fontSize:11, color:'#7B5EA7', marginTop:2 }}>
                            {new Date(a.created_at).toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric' })}
                          </div>
                        </div>
                        <a href={`${URL_REVISAO_CONTRATUAL}/revisao-contratual/salvo/${a.id}`} target="_blank" rel="noreferrer"
                          style={{ fontSize:11.5, fontWeight:700, color:'#7B2FBE', background:'#F3EBFF', padding:'6px 12px', borderRadius:8, whiteSpace:'nowrap', textDecoration:'none' }}>
                          Ver relatório →
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Análises de gestão de passivo empresarial vinculadas */}
            {analisesGestaoPassivoDoLead(cardAberto.id).length > 0 && (
              <div className="card" style={{ padding:16, marginBottom:14, border:'1px solid #0F766E30', background:'#F0FDF9' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#0F766E', textTransform:'uppercase', marginBottom:10 }}>🏢 Gestão de passivo empresarial</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {analisesGestaoPassivoDoLead(cardAberto.id).map(a => {
                    const LABEL_CAMINHO = {
                      negociacao_extrajudicial_simples: 'Negociação extrajudicial simples',
                      recuperacao_extrajudicial: 'Recuperação extrajudicial',
                      recuperacao_judicial: 'Recuperação judicial',
                      nao_elegivel_avaliar_alternativas: 'Não elegível — avaliar alternativas',
                    };
                    const caminho = LABEL_CAMINHO[a.resultado?.recomendacao?.caminho] || 'Recomendação não identificada';
                    return (
                      <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, background:'white', borderRadius:10, padding:'10px 12px', border:'1px solid rgba(15,118,110,0.1)' }}>
                        <div>
                          <div style={{ fontSize:12.5, fontWeight:600, color:'#1E0A3C' }}>{caminho}</div>
                          <div style={{ fontSize:11, color:'#7B5EA7', marginTop:2 }}>
                            {new Date(a.created_at).toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric' })}
                          </div>
                        </div>
                        <a href={`${URL_REVISAO_CONTRATUAL}/gestao-passivo/salvo/${a.id}`} target="_blank" rel="noreferrer"
                          style={{ fontSize:11.5, fontWeight:700, color:'#0F766E', background:'#ECFDF5', padding:'6px 12px', borderRadius:8, whiteSpace:'nowrap', textDecoration:'none' }}>
                          Ver relatório →
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Análises de superendividamento vinculadas */}
            {analisesSuperendividamentoDoLead(cardAberto.id).length > 0 && (
              <div className="card" style={{ padding:16, marginBottom:14, border:'1px solid #B4530930', background:'#FFFDF5' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#B45309', textTransform:'uppercase', marginBottom:10 }}>💳 Superendividamento</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {analisesSuperendividamentoDoLead(cardAberto.id).map(a => {
                    const enquadrado = a.resultado?.conclusao?.enquadramento === 'enquadrado';
                    return (
                      <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, background:'white', borderRadius:10, padding:'10px 12px', border:'1px solid rgba(180,83,9,0.1)' }}>
                        <div>
                          <div style={{ fontSize:12.5, fontWeight:600, color:'#1E0A3C' }}>
                            {a.resultado?.conclusao?.titulo || (enquadrado ? 'Enquadrado em superendividamento' : 'Não enquadrado')}
                          </div>
                          <div style={{ fontSize:11, color:'#7B5EA7', marginTop:2 }}>
                            {new Date(a.created_at).toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric' })}
                          </div>
                        </div>
                        <a href={`${URL_SUPERENDIVIDAMENTO}/superendividamento/salvo/${a.id}`} target="_blank" rel="noreferrer"
                          style={{ fontSize:11.5, fontWeight:700, color:'#B45309', background:'#FFFBEB', padding:'6px 12px', borderRadius:8, whiteSpace:'nowrap', textDecoration:'none' }}>
                          Ver relatório →
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Log de Observações ───────────────────────────────── */}
            <div style={{ marginBottom:20 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:10, textTransform:'uppercase' }}>
                Observações
              </label>

              {/* Histórico de notas */}
              {(cardAberto.notas || []).length > 0 && (
                <div style={{ marginBottom:10, maxHeight:180, overflowY:'auto', display:'flex', flexDirection:'column', gap:8 }}>
                  {[...(cardAberto.notas || [])].reverse().map((nota, i) => (
                    <div key={i} style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderRadius:7, padding:'8px 12px' }}>
                      <div style={{ fontSize:10, color:'var(--gray-400)', marginBottom:3 }}>
                        {new Date(nota.data_hora).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                      </div>
                      <div style={{ fontSize:13, color:'var(--text-primary)', whiteSpace:'pre-wrap' }}>{nota.texto}</div>
                    </div>
                  ))}
                  {/* Obs legada (campo obs antigo) */}
                  {cardAberto.obs && (
                    <div style={{ background:'var(--bg-secondary)', border:'1px dashed var(--border)', borderRadius:7, padding:'8px 12px', opacity:0.7 }}>
                      <div style={{ fontSize:10, color:'var(--gray-400)', marginBottom:3 }}>obs. anterior (importado)</div>
                      <div style={{ fontSize:13, color:'var(--text-primary)' }}>{cardAberto.obs}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Input nova nota */}
              <div style={{ display:'flex', gap:8 }}>
                <textarea
                  value={novaObs}
                  onChange={e => setNovaObs(e.target.value)}
                  placeholder="Adicionar uma observação..."
                  rows={2}
                  style={{ flex:1, padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)', boxSizing:'border-box', resize:'vertical' }}
                />
                <button
                  onClick={adicionarNota}
                  disabled={!novaObs.trim() || salvandoNota}
                  style={{ alignSelf:'flex-end', padding:'9px 14px', border:'none', borderRadius:6, background: funil.cor, color:'#fff', cursor:'pointer', fontWeight:600, fontSize:12, opacity: !novaObs.trim() ? 0.5 : 1 }}>
                  {salvandoNota ? '...' : 'Salvar'}
                </button>
              </div>
            </div>

            {/* ── Tarefas / Lembretes ──────────────────────────────── */}
            <div style={{ marginBottom:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <label style={{ fontSize:11, fontWeight:600, color:'var(--gray-400)', textTransform:'uppercase' }}>
                  Lembretes / Tarefas
                </label>
                <button onClick={() => setModalTarefa(v => !v)}
                  style={{ fontSize:11, padding:'4px 10px', borderRadius:6, border:`1px solid ${funil.cor}`, background:'transparent', color:funil.cor, cursor:'pointer', fontWeight:600 }}>
                  + Agendar
                </button>
              </div>

              {/* Form nova tarefa */}
              {modalTarefa && (
                <div style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderRadius:8, padding:14, marginBottom:10 }}>
                  <div style={{ marginBottom:10 }}>
                    <input
                      placeholder="Título do lembrete..."
                      value={formTarefa.titulo}
                      onChange={e => setFormTarefa(p => ({ ...p, titulo: e.target.value }))}
                      style={{ width:'100%', padding:'8px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--card-bg, #fff)', color:'var(--text-primary)', boxSizing:'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom:10 }}>
                    <input
                      type="datetime-local"
                      value={formTarefa.data_hora}
                      onChange={e => setFormTarefa(p => ({ ...p, data_hora: e.target.value }))}
                      style={{ width:'100%', padding:'8px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--card-bg, #fff)', color:'var(--text-primary)', boxSizing:'border-box' }}
                    />
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={salvarTarefa} disabled={salvandoTarefa || !formTarefa.titulo || !formTarefa.data_hora}
                      style={{ flex:1, background:funil.cor, color:'#fff', border:'none', padding:'8px', borderRadius:6, fontWeight:700, cursor:'pointer', fontSize:13 }}>
                      {salvandoTarefa ? 'Salvando...' : 'Salvar lembrete'}
                    </button>
                    <button onClick={() => setModalTarefa(false)}
                      style={{ background:'transparent', border:'1px solid var(--border)', padding:'8px 14px', borderRadius:6, cursor:'pointer', color:'var(--text-secondary)', fontSize:13 }}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {/* Lista de tarefas do lead */}
              {(() => {
                const tdLead = tarefasDoLead(cardAberto.id);
                if (tdLead.length === 0) return (
                  <div style={{ fontSize:12, color:'var(--gray-400)', padding:'8px 0' }}>Nenhum lembrete ainda.</div>
                );
                const agora = new Date();
                return (
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {tdLead.map(t => {
                      const dt = new Date(t.data_hora);
                      const vencida = !t.concluida && dt <= agora;
                      return (
                        <div key={t.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderRadius:7, background: t.concluida ? 'transparent' : vencida ? '#fff5f0' : 'var(--bg-secondary)', border: `1px solid ${t.concluida ? 'var(--border)' : vencida ? '#E07B3944' : 'var(--border)'}`, opacity: t.concluida ? 0.5 : 1 }}>
                          <span style={{ fontSize:14, color: t.concluida ? '#aaa' : vencida ? '#E07B39' : '#aaa', flexShrink:0 }}>🔔</span>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, color:'var(--text-primary)', fontWeight: vencida ? 600 : 400, textDecoration: t.concluida ? 'line-through' : 'none' }}>{t.titulo}</div>
                            <div style={{ fontSize:10, color: vencida && !t.concluida ? '#E07B39' : 'var(--gray-400)', marginTop:2 }}>
                              {dt.toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                              {vencida && !t.concluida && ' — Vencido'}
                            </div>
                          </div>
                          {!t.concluida && (
                            <button onClick={() => concluirTarefa(t.id)}
                              style={{ fontSize:11, padding:'3px 8px', borderRadius:5, border:'1px solid var(--border)', background:'transparent', color:'var(--text-secondary)', cursor:'pointer', whiteSpace:'nowrap' }}>
                              Concluir
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Enviar para Contratos */}
            {cardAberto.funil === 'advocacia_adm' && cardAberto.etapa === 'Cobrar Assinatura' && (
              <button onClick={() => enviarParaContratos(cardAberto)}
                style={{ width:'100%', marginBottom:10, background:'#f3e8ff', color:'var(--purple)', border:'2px solid var(--purple)', padding:'11px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:13 }}>
                📁 Enviar para Contratos
              </button>
            )}

            {/* Enviar para próxima fase */}
            {PROXIMA_FASE[cardAberto.funil] && (
              <button onClick={() => transferirParaProximaFase(cardAberto)}
                style={{ width:'100%', marginBottom:10, background:'#f0f7ff', color:'#4A90D9', border:'2px solid #4A90D9', padding:'11px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:13 }}>
                {LABEL_PROXIMA_FASE[cardAberto.funil]}
              </button>
            )}

            {/* Negócio perdido */}
            <button onClick={() => marcarPerdido(cardAberto)}
              style={{ width:'100%', marginBottom:10, background:'#fff5f5', color:'#E05454', border:'2px solid #E05454', padding:'11px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:13 }}>
              Negócio perdido
            </button>

            <div style={{ display:'flex', gap:10, marginBottom:10 }}>
              <button onClick={salvarCard}
                style={{ flex:1, background:funil.cor, color:'#fff', border:'none', padding:'11px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:14 }}>
                Salvar
              </button>
              <button onClick={() => { setCardAberto(null); setConfirmExcluir(false); setNovaObs(''); setModalTarefa(false); }}
                style={{ flex:1, background:'transparent', border:'1px solid var(--border)', padding:'11px', borderRadius:7, cursor:'pointer', color:'var(--text-secondary)', fontSize:14 }}>
                Cancelar
              </button>
            </div>

            {!confirmExcluir ? (
              <button onClick={() => setConfirmExcluir(true)}
                style={{ width:'100%', background:'transparent', border:'none', color:'var(--gray-400)', cursor:'pointer', fontSize:12, padding:'4px 0', textDecoration:'underline' }}>
                Excluir lead
              </button>
            ) : (
              <div style={{ background:'#fff5f5', border:'1px solid #E05454', borderRadius:8, padding:14, marginTop:4 }}>
                <div style={{ fontSize:13, color:'#E05454', fontWeight:600, marginBottom:10 }}>
                  Tem certeza que deseja excluir este lead? Essa ação não pode ser desfeita.
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={excluirLead}
                    style={{ flex:1, background:'#E05454', color:'#fff', border:'none', padding:'9px', borderRadius:6, fontWeight:700, cursor:'pointer', fontSize:13 }}>
                    Excluir definitivamente
                  </button>
                  <button onClick={() => setConfirmExcluir(false)}
                    style={{ flex:1, background:'transparent', border:'1px solid var(--border)', padding:'9px', borderRadius:6, cursor:'pointer', color:'var(--text-secondary)', fontSize:13 }}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal novo lead */}
      {modalNovo && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div className="card" style={{ width:440, padding:32 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
              <h3 style={{ margin:0, color:funil.cor }}>Novo Lead — {funil.label}</h3>
              <button onClick={() => setModalNovo(false)} style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:'var(--gray-400)' }}>×</button>
            </div>
            {[
              { label:'Nome', key:'nome', type:'text' },
              { label:'Telefone', key:'telefone', type:'text' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>{f.label}</label>
                <input type={f.type} value={novoLead[f.key]} onChange={e => setNovoLead({...novoLead, [f.key]: e.target.value})}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)', boxSizing:'border-box' }} />
              </div>
            ))}
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Projeto / Cliente</label>
              <select value={novoLead.projeto} onChange={e => setNovoLead({...novoLead, projeto: e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                <option value=''>— Selecione —</option>
                {projetosCad.map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Responsável</label>
              <select value={novoLead.responsavel} onChange={e => setNovoLead({...novoLead, responsavel: e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                <option value=''>— Selecione —</option>
                {colaboradores.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Prioridade</label>
              <select value={novoLead.prioridade} onChange={e => setNovoLead({...novoLead, prioridade: e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                {Object.entries(prioridadeConfig).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Origem</label>
              <select value={novoLead.origem} onChange={e => setNovoLead({...novoLead, origem: e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                <option value=''>— Não informado —</option>
                {Object.entries(ORIGENS).map(([k,v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Observação inicial</label>
              <textarea value={novoLead.obs} onChange={e => setNovoLead({...novoLead, obs: e.target.value})} rows={2}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)', boxSizing:'border-box', resize:'vertical' }} />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={adicionarLead}
                style={{ flex:1, background:funil.cor, color:'#fff', border:'none', padding:'11px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:14 }}>
                Salvar
              </button>
              <button onClick={() => setModalNovo(false)}
                style={{ flex:1, background:'transparent', border:'1px solid var(--border)', padding:'11px', borderRadius:7, cursor:'pointer', color:'var(--text-secondary)', fontSize:14 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal novo funil */}
      {modalNovoFunil && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div className="card" style={{ width:480, padding:32 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
              <h3 style={{ margin:0, color:'var(--purple)' }}>Novo Funil</h3>
              <button onClick={() => setModalNovoFunil(false)} style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:'var(--gray-400)' }}>×</button>
            </div>
            {[
              { label:'Nome do funil', key:'label', type:'text', placeholder:'Ex: Vuli — Operações' },
              { label:'Identificador único (sem espaços)', key:'key', type:'text', placeholder:'Ex: vuli_operacoes' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>{f.label}</label>
                <input type={f.type} value={formFunil[f.key]} placeholder={f.placeholder}
                  onChange={e => setFormFunil({...formFunil, [f.key]: e.target.value})}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)', boxSizing:'border-box' }} />
              </div>
            ))}
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Segmento</label>
              <select value={formFunil.segmento} onChange={e => setFormFunil({...formFunil, segmento: e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)' }}>
                <option value='advocacia'>Advocacia</option>
                <option value='saude'>Saúde</option>
                <option value='infoprodutos'>Infoprodutos</option>
                <option value='vuli'>Vuli (interno)</option>
                <option value='outro'>Outro</option>
              </select>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:8, textTransform:'uppercase' }}>Cor do funil</label>
              <div style={{ display:'flex', gap:8 }}>
                {['#4A90D9','#6B2FA0','#D4789B','#C9A84C','#5BAD8F','#E05454','#9B7FD4','#0891b2'].map(cor => (
                  <div key={cor} onClick={() => setFormFunil({...formFunil, cor, corFundo: cor+'22'})}
                    style={{ width:28, height:28, borderRadius:'50%', background:cor, cursor:'pointer', border: formFunil.cor===cor ? '3px solid #333' : '2px solid transparent' }} />
                ))}
              </div>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Primeira etapa</label>
              <input value={formFunil.etapas[0]} onChange={e => setFormFunil({...formFunil, etapas: [e.target.value]})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)', boxSizing:'border-box' }} />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={criarFunil}
                style={{ flex:1, background:'var(--purple)', color:'white', border:'none', padding:'11px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:14 }}>
                Criar Funil
              </button>
              <button onClick={() => setModalNovoFunil(false)}
                style={{ flex:1, background:'transparent', border:'1px solid var(--border)', padding:'11px', borderRadius:7, cursor:'pointer', color:'var(--text-secondary)', fontSize:14 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
